import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SocialShare } from '@components/common';
import { getLatestBlogPosts } from '@lib/blog';

export default function LatestBlogPost() {
  const posts = getLatestBlogPosts(3);

  if (posts.length === 0) {
    return null;
  }

  return (
    <section className="py-24 bg-muted">
      <div className="container-custom">
        <div className="text-center mb-12 animate-fade-up">
          <span className="text-xs font-medium text-secondary tracking-[0.2em] uppercase">
            Insights From The News
          </span>
          <div className="mx-auto mt-4 mb-5 h-px w-12 bg-secondary/40" />
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground tracking-tight">
            Latest Thinking
          </h2>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-up"
          style={{ animationDelay: '0.15s' }}
        >
          {posts.map((post, index) => (
            <Link
              key={post.slug}
              to={`/news/${post.slug}`}
              className="group bg-white rounded-xl border border-border p-6 flex flex-col hover:border-secondary hover:shadow-lg transition-all h-full relative"
              style={{ animationDelay: `${0.15 + index * 0.1}s` }}
            >
              <div className="flex flex-col h-full items-start">
                {post.image && (
                  <div className="-mx-6 -mt-6 mb-6 overflow-hidden rounded-t-xl">
                    <img
                      src={post.image}
                      alt={post.title}
                      loading="lazy"
                      className="w-full aspect-[16/9] object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                )}
                <span className="inline-flex w-fit px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-medium mb-4">
                  {post.category}
                </span>

                <h3 className="text-lg font-heading font-semibold text-foreground group-hover:text-secondary transition-colors mb-3 line-clamp-2">
                  {post.title}
                </h3>

                <p className="text-muted-foreground text-sm leading-relaxed mb-4 flex-grow line-clamp-3">
                  {post.excerpt}
                </p>

                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Date(post.date).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {post.readTime}
                  </span>
                </div>

                <div className="flex items-center justify-between w-full pt-4 border-t border-border/50 mt-auto">
                  <span className="text-sm font-medium text-secondary">
                    Read article
                    <ArrowRight className="h-4 w-4 ml-1.5 transition-transform group-hover:translate-x-1 inline" />
                  </span>
                  <SocialShare
                    slug={post.slug}
                    title={post.title}
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-10 animate-fade-up" style={{ animationDelay: '0.45s' }}>
          <Link
            to="/news"
            className="inline-flex items-center text-sm font-medium text-secondary hover:text-secondary transition-colors"
          >
            View all posts
            <ArrowRight className="h-4 w-4 ml-1.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
